import React, { type FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { Form, Formik, FormikHelpers } from 'formik';

import { AdditionalInfoModalFormData } from './types';
import type { ReduxState } from 'app/redux/types';

import { formSubmissionSuccessDelay } from 'app/constants/formConstants';
import { LoadingStatus } from 'app/constants/SideEffectStatus';
import { Modal } from 'app/design-system/components/Modal';
import { addPersonalData } from 'app/lib/account/reducers/clientInfo/clientInfoReducer';

import { ModalBody } from './components/Body';
import { ModalFooter } from './components/Footer';
import type { ErrorMessageKey } from './utils';
import {
  getApiMethod,
  getDataErrorMessage,
  prepareDataForBackend,
} from './utils';

export const INITIAL_VALUE: AdditionalInfoModalFormData = {
  value: '',
};

type PersonalDataModalProps = {
  title: string;
  showModal: boolean;
  variant: 'city' | 'vatNumber' | 'email';
  handleClose: VoidFunction;
  validator?: (values: {value?: string}) => {value?: string};
  selector: (state: ReduxState) => string | undefined;
};

export const AdditionalInfoModal: FC<PersonalDataModalProps> = ({
  title,
  showModal,
  variant,
  handleClose,
  validator,
  selector,
}) => {
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();

  const handleSubmit = useCallback(
    async (
      values: AdditionalInfoModalFormData,
      actions: FormikHelpers<AdditionalInfoModalFormData>,
    ) => {
      const data = prepareDataForBackend({ form: values, variant });
      const apiMethod = getApiMethod(variant);
      apiMethod(data)
        .then(() => {
          // dispatchGaEvent(createGaEvent({ event: 'profileAddNameConfirm' }));
          actions.setStatus(LoadingStatus.SUCCESS);

          setTimeout(() => {
            dispatch(addPersonalData(data));
            handleClose();
          }, formSubmissionSuccessDelay);
        })
        .catch((err) => {
          const alert = getDataErrorMessage[err.response.status as ErrorMessageKey];
          actions.setStatus(err.response.status);
          actions.setErrors({ alert });
          console.error('AdditionalInfoModal error', err);
        });
    },
    [search, pathname],
  );

  return (
    <Modal isOpen={showModal}
           closable
           onClose={handleClose}
           data-test="Modal-personalData">
      <Modal.Header>
        {title}
      </Modal.Header>
      <Formik validate={validator}
              onSubmit={handleSubmit}
              initialValues={INITIAL_VALUE}>
        <Form noValidate>
          <ModalBody variant={variant}
                     selector={selector} />
          <ModalFooter handleClose={handleClose} />
        </Form>
      </Formik>
    </Modal>
  );
};
